import { stringifySearchWith } from '@tanstack/react-router';

/**
 * How search params are read and written, for both planes.
 *
 * Extracted from the console's router when the member portal appeared, because
 * the guarantee below is not console-specific: any app in this repository that
 * puts an id in a query string needs it, and the failure it prevents is silent.
 * See the note on snowflakes for what that cost the first time.
 */
/**
 * Every search value, as the string it was in the address bar.
 *
 * That is the entire guarantee, and it holds for every value rather than for
 * some of them: nothing between the address bar and a route's `validateSearch`
 * decides what a value looks like. Typing it is the route's job, and every
 * `validateSearch` in this app already coerces what it needs (see `num` in
 * routes/audit.tsx), so nothing depends on the router guessing.
 *
 * It is written out by hand because `parseSearchWith` cannot make that promise,
 * whatever parser it is handed. It runs the query string through `qss.decode`
 * first — which turns `"120"` into `120` and `"true"` into `true` — and then
 * consults the supplied parser only for the values still left as strings. An
 * identity parser passed to it therefore changes nothing about the values
 * already converted, which is most of the interesting ones.
 *
 * What that cost: the router's default parser is `JSON.parse`, so
 * `?discordID=284453805921927168` arrived as 284453805921927170 and the console
 * read an account nobody had asked for — a confident wrong answer rather than
 * an error, which is the worst kind. Handing `parseSearchWith` an identity
 * parser looked like the fix and was not. Snowflakes survived it by accident:
 * `decode` guards its numeric coercion with a round-trip check
 * (`+str + '' === str`), and a value past Number.MAX_SAFE_INTEGER usually fails
 * that check because its shortest representation as a double is some other
 * number. Usually, not always — `?discordID=1000000000000000000` round-trips
 * perfectly and was converted — and nothing shorter was protected at all, so
 * `?vmid=120` arrived as a number and every `typeof value === 'string'` test in
 * this app quietly said no to it.
 *
 * Repeated keys are the one thing a query string can state that this shape
 * cannot hold. `?vmid=1&vmid=2` keeps the first, matching what
 * `URLSearchParams.get` answers for that URL; the alternative is an array,
 * which is precisely the "sometimes a value is not a string" being removed
 * here. Nothing in this app writes a repeat — `stringifySearch` sets each key
 * once — so one only ever arrives hand-assembled, and there the link as written
 * should outrank whatever was appended to the end of it.
 *
 * The prototype-less object is deliberate too, and is what `qss.decode` used as
 * well. On a plain `{}`, writing `__proto__` goes to a setter rather than to
 * the object, so `?__proto__=x` would silently vanish — a key the URL stated
 * and the search does not have. Here it is an ordinary key like any other.
 */
function parseSearchAsStrings(searchStr: string): Record<string, string> {
  // `URLSearchParams` drops one leading `?` itself, which is the same thing
  // `parseSearchWith` does by hand before it decodes.
  const params = new URLSearchParams(searchStr);
  const search = Object.create(null) as Record<string, string>;

  for (const [key, value] of params) {
    if (!(key in search)) search[key] = value;
  }

  return search;
}

/**
 * How search params are read and written.
 *
 * The serialiser is still the library's own, because for string values it is
 * already this parser's exact inverse: it sets each key once on a
 * `URLSearchParams` and percent-encodes the result. Only the reading direction
 * was ever lossy.
 *
 * The pair round-trips values exactly and normalises spelling, which is a
 * distinction worth stating because a query string has more than one way to
 * write the same thing: `?a=x%20y` comes back as `?a=x+y` and `?a` comes back
 * as `?a=`, both of which parse to the value they started as. So a first cycle
 * may rewrite the URL; every cycle after it is a fixed point.
 *
 * Exported so tests build routers that serialise exactly as the app does. A
 * test router left on the defaults writes `?vmid=%22120%22` where the app
 * writes `?vmid=120`, which makes any assertion about where a link points
 * quietly meaningless.
 */
export const SEARCH_CONFIG = {
  parseSearch: parseSearchAsStrings,
  stringifySearch: stringifySearchWith(String),
} as const;

